import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Add trailing slash to URL path (except root and paths with query params/hash)
 */
export function addTrailingSlash(path: string): string {
  if (path === '/' || path === '') {
    return '/';
  }
  // Don't add trailing slash if it already has one, or if it has query params/hash
  if (path.endsWith('/') || path.includes('?') || path.includes('#')) {
    return path;
  }
  return `${path}/`;
}

/**
 * Formats location names from URL slugs to proper title case
 * @param locationSlug - The location slug (e.g., "united-states", "new-york")
 * @returns String with proper location title case formatting
 */
export function formatLocationName(locationSlug: string): string {
  if (!locationSlug) return '';
  
  // Convert slug to basic title case first
  const basicFormatted = locationSlug.replace(/-/g, ' ');
  
  // Apply toTitleCase with additional location-specific handling
  const titleCased = toTitleCase(basicFormatted);
  
  // Handle special location cases that need manual correction
  const locationSpecialCases: Record<string, string> = {
    'district of columbia': 'District of Columbia',
    'united states': 'United States',
    'united states of america': 'United States of America',
    'new york': 'New York',
    'los angeles': 'Los Angeles',
    'las vegas': 'Las Vegas',
    'san francisco': 'San Francisco',
    'san diego': 'San Diego',
    'san antonio': 'San Antonio',
    'fort worth': 'Fort Worth',
    'fort lauderdale': 'Fort Lauderdale',
    'west virginia': 'West Virginia',
    'new hampshire': 'New Hampshire',
    'new jersey': 'New Jersey',
    'new mexico': 'New Mexico',
    'north carolina': 'North Carolina',
    'north dakota': 'North Dakota',
    'south carolina': 'South Carolina',
    'south dakota': 'South Dakota',
    'rhode island': 'Rhode Island'
  };
  
  const lowerCased = titleCased.toLowerCase();
  return locationSpecialCases[lowerCased] || titleCased;
}

/**
 * Converts text to Title Case with special handling for occupation names
 * @param text - The text to convert to title case
 * @returns String with proper title case formatting
 */
export function toTitleCase(text: string): string {
  if (!text) return '';
  
  // Handle common abbreviations and special cases in occupations
  const specialCases: Record<string, string> = {
    'ceo': 'CEO',
    'cfo': 'CFO',
    'cto': 'CTO',
    'hr': 'HR',
    'it': 'IT',
    'rn': 'RN',
    'lpn': 'LPN',
    'md': 'MD',
    'phd': 'PhD',
    'cpa': 'CPA',
    'ui': 'UI',
    'ux': 'UX',
    'api': 'API',
    'seo': 'SEO',
    'qa': 'QA',
    'ai': 'AI',
    'ml': 'ML',
    'ios': 'iOS',
    'android': 'Android',
    'aws': 'AWS',
    'gcp': 'GCP',
    'css': 'CSS',
    'html': 'HTML',
    'sql': 'SQL',
    'crm': 'CRM',
    'erp': 'ERP',
    'saas': 'SaaS',
    'b2b': 'B2B',
    'b2c': 'B2C',
    'ecommerce': 'E-commerce',
    'ecom': 'E-com'
  };

  // Articles, prepositions, and conjunctions that should be lowercase (except at start)
  const minorWords = [
    'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'if', 'in', 'nor', 
    'of', 'on', 'or', 'so', 'the', 'to', 'up', 'yet', 'with'
  ];

  return text
    .toLowerCase()
    .split(/[\s-]+/)
    .map((word, index) => {
      const cleanWord = word.replace(/[^\w]/g, '');
      
      // Check if it's a special case (abbreviation)
      if (specialCases[cleanWord.toLowerCase()]) {
        return word.replace(cleanWord, specialCases[cleanWord.toLowerCase()]);
      }
      
      // Don't lowercase minor words unless they're the first word
      if (index > 0 && minorWords.includes(cleanWord.toLowerCase())) {
        return word;
      }
      
      // Standard title case: capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Formats a number as currency
 * @param amount - The amount to format
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}
